// Writes render before the server confirms them, which is what makes the app feel quick —
// and what makes closing the tab mid-write lose work the user has already seen land. Nothing
// cancels a request on navigation, so leaving a page is safe; leaving the *document* is not.
//
// Anything with an unconfirmed write registers here and the browser asks before discarding
// it. Registrations are only read at unload, so nothing needs to be reactive.

type PendingWrites = () => boolean;

const claimants: PendingWrites[] = [];
let bound = false;

function onBeforeUnload(event: BeforeUnloadEvent) {
  if (!hasPendingWrites()) return;

  event.preventDefault();
  // Older browsers show their own message and want this set; the text is never used.
  event.returnValue = '';
}

/** Whether anything on the page is holding a write the server has not confirmed. */
export function hasPendingWrites() {
  return claimants.some((hasPending) => hasPending());
}

/**
 * Declares that this caller may be holding unconfirmed writes, until the returned function
 * is called.
 *
 * Pass a predicate for work whose state is already tracked somewhere — a table's unsaved
 * cells. For work with a beginning and an end, pass `() => true` and treat the returned
 * function as the release, the way a loading spinner is started and stopped.
 */
export function registerPendingWrites(hasPending: PendingWrites) {
  claimants.push(hasPending);

  if (!bound && typeof window !== 'undefined') {
    window.addEventListener('beforeunload', onBeforeUnload);
    bound = true;
  }

  return () => {
    const index = claimants.lastIndexOf(hasPending);
    if (index !== -1) claimants.splice(index, 1);

    if (claimants.length === 0 && bound && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', onBeforeUnload);
      bound = false;
    }
  };
}
